import { useEffect, useRef, useState, type ReactNode } from "react";
import { api, isAdminRole, type BillingView, type Catalogue, type CataloguePlan, type EntitlementView, type GrantRow, type Offer, type OrderView, type UseRow } from "../api";
import { link } from "../App";
import { GIB, MIB, meterPercent, priceLabel, sizeLabel, startHelps } from "../billing";
import { errorText, useT } from "../i18n";
import { useSession } from "../session";
import { formatDay } from "../time";
import { Disclosure } from "../Disclosure";
import { useRefreshHandler } from "../Refresh";
import { usePagedList } from "../pagedList";
import { ShowMore } from "../ShowMore";
import { billingDocument, openDodo, openPaddle, type CheckoutSignal } from "../checkoutFrame";

/**
 * Billing (PLAN.md 4.24, 4.26): the member's plan, stamps and storage, what the payment provider
 * sells, and the history. Rights come only from the provider's signed events (or an admin's grant).
 * Everything the member already sent stays readable whatever this page says.
 */
export function Billing() {
  const t = useT();
  const { me, refresh } = useSession();
  const [view, setView] = useState<BillingView | null>(null);
  const [catalogue, setCatalogue] = useState<Catalogue | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      const [v, c] = await Promise.all([api.billing(), api.catalogue()]);
      setView(v);
      setCatalogue(c);
      setError(null);
    } catch (err) {
      setError(errorText(err));
    }
  };
  useEffect(() => { if (me.kind !== "service") void load(); }, [me.id, me.kind]);
  useRefreshHandler(async () => { if (me.kind !== "service") await load(); });

  // One purchase at a time: the provider's checkout opens in a panel on this page.
  const [purchase, setPurchase] = useState<Purchase | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Set at the click, before the server answers, so a second click cannot start a second checkout (PLAN.md 4.27 R3).
  const [creating, setCreating] = useState(false);
  async function upgrade(priceId: string) {
    if (creating) return;
    setError(null);
    setNotice(null);
    // The checkout needs this page's CSP exception, which only a document loaded at /billing has.
    if (!billingDocument()) {
      location.assign(`/billing?buy=${encodeURIComponent(priceId)}`);
      return;
    }
    setCreating(true);
    try {
      const r = await api.checkout(priceId);
      setPurchase({ orderId: r.order.id, provider: r.checkout.provider, environment: r.checkout.environment, checkoutId: r.checkout.id, url: r.checkout.url });
    } catch (err) {
      setError(errorText(err));
    }
    setCreating(false);
  }
  // Arriving with ?buy=<price> (from another page) opens that checkout; ?_ptxn=<transaction> is Paddle's
  // own link to a transaction (its default payment link points here), which only needs opening.
  const started = useRef(false);
  useEffect(() => {
    if (!catalogue || started.current || me.kind === "service") return;
    started.current = true;
    const params = new URLSearchParams(location.search);
    const buy = params.get("buy");
    const ptxn = params.get("_ptxn");
    if (buy || ptxn) history.replaceState(history.state, "", "/billing");
    if (buy && (catalogue.member_checkout || isAdminRole(me.role))) void upgrade(buy);
    else if (ptxn && catalogue.provider === "paddle") {
      // Paddle's link names its transaction; our purchase is found by it, so the page can follow it.
      void api.checkoutStatus(ptxn).then(
        (r) => setPurchase({ orderId: r.order.id, provider: "paddle", environment: catalogue.environment, checkoutId: ptxn, url: null }),
        () => setPurchase({ orderId: null, provider: "paddle", environment: catalogue.environment, checkoutId: ptxn, url: null }),
      );
    }
  }, [catalogue]);
  const finished = async (paid: boolean) => {
    setPurchase(null);
    if (paid) {
      setNotice(t.billing.checkoutDone);
      await Promise.all([load(), refresh()]);
    }
  };

  if (me.kind === "service") {
    return (
      <div className="billing-page">
        <div className="page-head"><h1>{t.billing.title}</h1></div>
        <p className="muted">{t.billing.serviceNote}</p>
      </div>
    );
  }

  return (
    <div className="billing-page">
      <div className="page-head">
        <h1>{t.billing.title}</h1>
        <p className="muted small">{t.billing.pageNote}</p>
      </div>
      <p className="note small" role="note">{t.billing.previewNote}</p>
      {error && <p className="error" role="alert">{error}</p>}
      {notice && <p className="note" role="status">{notice}</p>}
      {!view && !error && <p className="muted">{t.common.loading}</p>}
      {view && catalogue && (
        <>
          <div className="grid2 billing-top">
            <PlanCard view={view} />
            <StampsCard view={view} />
          </div>
          <StorageCard view={view} />
          {purchase && <CheckoutPanel purchase={purchase} catalogue={catalogue} email={me.email ?? null} onFinished={(paid) => void finished(paid)} />}
          {!catalogue.member_checkout && <p className="note small" role="note">{t.billing.notSelling}</p>}
          <Chooser
            catalogue={catalogue}
            view={view}
            action={!catalogue.member_checkout && !isAdminRole(me.role) ? undefined : (o) => (
              <button type="button" className="primary" disabled={purchase !== null || creating} aria-busy={creating} onClick={() => void upgrade(o.price_id)}>
                {o.kind === "subscription" ? t.billing.upgrade : t.billing.buyPack}
              </button>
            )}
          />
          <Rights entitlements={view.entitlements} />
          <History />
        </>
      )}
    </div>
  );
}

type Purchase = { orderId: string | null; provider: string; environment: string; checkoutId: string; url: string | null };

/**
 * The provider's checkout inside this page (PLAN.md 4.26 P3). Rights come only from the provider's
 * signed event, never from this page: after the frame says "paid", the page asks the server about the
 * purchase until its event has arrived, then shows the new plan or stamps.
 */
function CheckoutPanel({ purchase, catalogue, email, onFinished }: { purchase: Purchase; catalogue: Catalogue; email: string | null; onFinished: (paid: boolean) => void }) {
  const t = useT();
  const [phase, setPhase] = useState<"open" | "confirming" | "activating" | "slow" | "error">("open");
  const [detail, setDetail] = useState<string | null>(null);
  const closeFrame = useRef<(() => void) | null>(null);
  const finishedRef = useRef(false);
  const panel = useRef<HTMLElement | null>(null);
  useEffect(() => {
    void panel.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);
  const finish = (paid: boolean) => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    closeFrame.current?.();
    onFinished(paid);
  };
  const onSignal = (s: CheckoutSignal) => {
    if (s === "completed") setPhase("confirming");
    else if (s === "error") setPhase("error");
  };

  useEffect(() => {
    // Closed while the script loaded: the checkout must not appear after all (the open checks the
    // signal), and a close from this panel never closes the checkout of a panel opened after it.
    const panelGone = new AbortController();
    const alive = () => !panelGone.signal.aborted;
    void (async () => {
      try {
        if (purchase.provider === "paddle") {
          if (!catalogue.client_token) throw new Error(t.billing.checkoutNotReady);
          const close = await openPaddle({ token: catalogue.client_token, environment: purchase.environment, transactionId: purchase.checkoutId, email, frameClass: "checkout-frame", onSignal, signal: panelGone.signal });
          if (!alive()) close();
          else closeFrame.current = close;
        } else if (purchase.provider === "dodo") {
          if (!purchase.url) throw new Error(t.billing.checkoutNotReady);
          try {
            const close = await openDodo({ checkoutUrl: purchase.url, environment: purchase.environment, elementId: "checkout-frame", onSignal, signal: panelGone.signal });
            if (!alive()) close();
            else closeFrame.current = close;
          } catch {
            // The fallback of P3: Dodo's own page, which returns here.
            if (alive()) location.assign(purchase.url);
          }
        }
      } catch (err) {
        if (alive()) {
          setDetail(err instanceof Error ? err.message : String(err));
          setPhase("error");
        }
      }
    })();
    return () => {
      panelGone.abort();
      closeFrame.current?.();
      closeFrame.current = null;
    };
  }, [purchase.checkoutId]);

  // The purchase is asked about every 3 s while the panel is open; the provider's event completes it.
  useEffect(() => {
    const orderId = purchase.orderId;
    if (!orderId) return;
    const startedAt = Date.now();
    let alive = true;
    const timer = setInterval(() => {
      void api.checkoutStatus(orderId).then(
        (r) => {
          // An answer that arrives after the panel closed changes nothing.
          if (!alive) return;
          // "Active" only when the rights hold; a payment the provider confirmed may still be activating.
          if (r.rights_active) finish(true);
          else if (Date.now() - startedAt > 120_000) setPhase((p) => (p === "confirming" || p === "activating" ? "slow" : p));
          else if (r.order.status === "completed") setPhase((p) => (p === "error" ? p : "activating"));
        },
        () => undefined,
      );
    }, 3000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [purchase.orderId]);

  return (
    <section ref={panel} className="card checkout-panel" aria-label={t.billing.checkoutTitle}>
      <div className="checkout-head">
        <h2>{t.billing.checkoutTitle}</h2>
        <button type="button" className="link small" onClick={() => finish(false)}>{t.billing.checkoutClose}</button>
      </div>
      {purchase.environment !== "live" && <p className="small note">{t.billing.checkoutTest}</p>}
      {purchase.provider === "fake" && <p className="small muted">{t.billing.checkoutFake}</p>}
      {phase === "confirming" && <p className="small" role="status">{t.billing.checkoutConfirming}</p>}
      {phase === "activating" && <p className="small" role="status">{t.billing.checkoutActivating}</p>}
      {phase === "slow" && <p className="small" role="status">{t.billing.checkoutSlow}</p>}
      {phase === "error" && <p className="error small" role="alert">{t.billing.checkoutError}{detail ? ` (${detail})` : ""}</p>}
      <div className="checkout-frame" id="checkout-frame" />
    </section>
  );
}

/** The provider's customer portal: invoices, payment method, changing or cancelling the subscription. */
function PortalButton() {
  const t = useT();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function open() {
    setError(null);
    setBusy(true);
    // The tab opens at the click (pop-up blockers allow that), then goes to the fresh link.
    const win = window.open("", "_blank");
    try {
      const url = await api.portal();
      if (win) {
        win.opener = null;
        win.location.href = url;
      } else location.assign(url);
    } catch (err) {
      win?.close();
      setError(errorText(err));
    }
    setBusy(false);
  }
  return (
    <>
      <button type="button" className="small" disabled={busy} onClick={() => void open()}>{t.billing.portal}</button>
      <p className="small muted">{t.billing.portalNote}</p>
      {error && <p className="error small" role="alert">{error}</p>}
    </>
  );
}

/**
 * How the plan goes on (PLAN.md 4.27 R6): a provider subscription renews, ends on a date, or has not
 * said yet; a plan given by hand runs to its date and does not renew.
 */
function RenewalLine({ subscription: s }: { subscription: BillingView["subscription"] }) {
  const t = useT();
  const day = (at: number | null) => (at === null ? "" : formatDay(at * 1000));
  if (s.renewal) {
    if (s.renewal.auto_renew === true && s.activated_through !== null) return <p className="small">{t.billing.renewsOn(day(s.activated_through))}</p>;
    if (s.renewal.auto_renew === false) return <p className="small">{t.billing.endsOn(day(s.renewal.cancel_at ?? s.activated_through))}</p>;
    return <p className="small muted">{t.billing.renewalUnknown}</p>;
  }
  if (s.activated_through === null) return null;
  return <p className="small">{t.billing.activatedUntil(day(s.activated_through))} {t.billing.noAutoRenew}</p>;
}

function PlanCard({ view }: { view: BillingView }) {
  const t = useT();
  const name = t.billing.planNames[view.plan.id] ?? view.plan.id;
  return (
    <section className="card">
      <h2>{t.billing.planTitle}</h2>
      <p className="plan-line"><strong>{t.billing.planLine(name, view.plan.monthly_units)}</strong></p>
      <p className="small">{t.billing.attachmentLine(sizeLabel(view.plan.attachment_bytes))}</p>
      <RenewalLine subscription={view.subscription} />
      <p className="small muted">{t.billing.refreshOn(formatDay(view.stamps.period.ends_at * 1000))}</p>
      {view.has_portal && <PortalButton />}
    </section>
  );
}

function StampsCard({ view }: { view: BillingView }) {
  const t = useT();
  const s = view.stamps;
  return (
    <section className="card">
      <h2>{t.billing.stampsTitle}</h2>
      <p className="big-number" aria-live="polite">{t.billing.stampsLeft(s.remaining)}</p>
      <Meter value={s.period.consumed} max={s.period.granted} label={t.billing.monthMeterName} text={t.billing.monthMeter(s.period.consumed, s.period.granted)} />
      {s.held_pending > 0 && <p className="small">{t.billing.held(s.held_pending)}</p>}
      {s.expiring_soon.length > 0 && (
        <>
          <p className="small"><strong>{t.billing.expiring}</strong></p>
          <ul className="plain small">
            {s.expiring_soon.map((e) => <li key={e.expires_at}>{t.billing.expiringItem(e.units, formatDay(e.expires_at * 1000))}</li>)}
          </ul>
          {s.expiring_soon_truncated && <p className="small muted">{t.billing.expiringMore}</p>}
        </>
      )}
      <p className="small muted">{t.billing.stampExplain}</p>
    </section>
  );
}

function StorageCard({ view }: { view: BillingView }) {
  const t = useT();
  const { used_bytes: used, quota_bytes: quota, over_quota: over } = view.storage;
  return (
    <section className="card">
      <h2>{t.billing.storageTitle}</h2>
      <Meter value={used} max={quota} label={t.billing.storageMeterName} text={t.billing.storageLine(sizeLabelExact(used), sizeLabelExact(quota))} over={over} />
      {over && <p className="warn small" role="alert">{t.billing.overQuota}</p>}
      <p className="small muted">{t.billing.storageNote}</p>
    </section>
  );
}

/** Used storage is rarely a round number: show it with one decimal in the unit that fits. */
function sizeLabelExact(bytes: number): string {
  if (bytes >= GIB) return `${(Math.round((bytes / GIB) * 10) / 10).toString()} GiB`;
  if (bytes >= MIB) return `${(Math.round((bytes / MIB) * 10) / 10).toString()} MiB`;
  return `${Math.round(bytes / 1024)} KiB`;
}

/**
 * A bar with its numbers. Only the bar is clamped to 100%; the text and the accessible value state
 * the real figures, over quota included.
 */
export function Meter({ value, max, label, text, over = false }: { value: number; max: number; label: string; text: string; over?: boolean }) {
  return (
    <div className="meter-wrap">
      <div className={over ? "meter over" : "meter"} role="meter" aria-label={label} aria-valuemin={0} aria-valuemax={Math.max(max, value)} aria-valuenow={value} aria-valuetext={text}>
        <span style={{ width: `${meterPercent(value, max)}%` }} />
      </div>
      <p className="small meter-text">{text}</p>
    </div>
  );
}

/**
 * Our own levels (Free, Legacy) beside what the payment provider sells (PLAN.md 4.26). `action` renders
 * the button of an offer; without it (the public page) the cards only inform.
 */
export function Chooser({ catalogue, view, action }: { catalogue: Catalogue; view: BillingView | null; action?: (offer: Offer) => ReactNode }) {
  const t = useT();
  const plan = (id: string) => catalogue.plans.find((p) => p.id === id);
  const detail = (p: Pick<CataloguePlan, "monthly_units" | "base_storage_bytes" | "attachment_bytes">) => t.billing.planDetail(p.monthly_units, sizeLabel(p.base_storage_bytes), sizeLabel(p.attachment_bytes));
  const now = Date.now() / 1000;
  const current = view?.plan.id;
  const offers = catalogue.offers.filter((o) => {
    if (o.kind !== "subscription" || !view) return true;
    // A subscription that would add nothing (Legacy and Start) is not offered, unless one runs: then the portal changes it.
    return startHelps(view, plan(o.plan_id ?? ""), view.entitlements, now);
  });
  const hidden = catalogue.offers.length - offers.length;
  const track = useRef<HTMLDivElement | null>(null);
  const slide = (dir: 1 | -1) => track.current?.scrollBy({ left: dir * Math.max(240, track.current.clientWidth * 0.8), behavior: "smooth" });
  return (
    <section className="card chooser">
      <div className="chooser-head">
        <h2>{t.billing.chooseTitle}</h2>
        <span className="carousel-nav">
          <button type="button" className="small" aria-label={t.billing.carouselPrev} onClick={() => slide(-1)}>‹</button>
          <button type="button" className="small" aria-label={t.billing.carouselNext} onClick={() => slide(1)}>›</button>
        </span>
      </div>
      <div className="offers" ref={track} tabIndex={0} role="group" aria-label={t.billing.chooseTitle}>
        {offers.map((o) => {
          const p = o.plan_id ? plan(o.plan_id) : undefined;
          return (
            <article key={o.price_id} className={o.kind === "subscription" ? "offer paid featured" : "offer paid"}>
              <h3>{o.name}</h3>
              <p className="price">
                {priceLabel(o.amount, o.currency, t.locale)}
                {o.interval && <span className="muted small"> {t.billing.every(o.interval, o.interval_count)}</span>}
              </p>
              {o.kind === "subscription" && p && <p className="small">{detail(p)}</p>}
              {o.kind === "pack" && <p className="small">{t.billing.packDetail(o.stamps, o.stamp_months, sizeLabel(o.extra_storage_bytes), sizeLabel(o.attachment_bytes), o.attachment_months)}</p>}
              {o.description && <p className="small muted">{o.description}</p>}
              {action?.(o)}
            </article>
          );
        })}
        {(["free", "legacy"] as const).map((id) => {
          const p = plan(id);
          if (!p) return null;
          return (
            <article key={id} className={current === id ? "offer current" : "offer"}>
              <h3>{t.billing[id].name}{current === id && <span className="tag">{t.billing.current}</span>}</h3>
              <p className="small">{detail(p)}</p>
              <p className="small muted">{t.billing[id].text}</p>
            </article>
          );
        })}
      </div>
      {hidden > 0 && <p className="small muted">{t.billing.startNotNeeded}</p>}
      {catalogue.offers.length === 0 && <p className="small muted">{t.billing.nothingOnSale}</p>}
    </section>
  );
}

function rightWhat(e: EntitlementView, t: ReturnType<typeof useT>): string {
  const parts: string[] = [];
  if (e.monthly_units > 0) parts.push(t.billing.rightStamps(e.monthly_units));
  if (e.base_storage_bytes > 0) parts.push(t.billing.rightStorage(sizeLabel(e.base_storage_bytes)));
  if (e.extra_storage_bytes > 0) parts.push(t.billing.rightExtraStorage(sizeLabel(e.extra_storage_bytes)));
  if (e.attachment_bytes > 0) parts.push(t.billing.rightFiles(sizeLabel(e.attachment_bytes)));
  return `${t.billing.rightSource[e.source] ?? e.source}: ${parts.join(", ")}`;
}

function Rights({ entitlements }: { entitlements: EntitlementView[] }) {
  const t = useT();
  if (entitlements.length === 0) return null;
  return (
    <Disclosure title={t.billing.rightsTitle}>
      <p className="small muted">{t.billing.rightsNote}</p>
      <ul className="rows">
        {entitlements.map((e) => (
          <li key={e.id}><span className="grow small">{t.billing.rightLine(rightWhat(e, t), formatDay(e.starts_at * 1000), e.ends_at === null ? null : formatDay(e.ends_at * 1000))}</span></li>
        ))}
      </ul>
    </Disclosure>
  );
}

type Tab = "uses" | "grants" | "orders";

function History() {
  const t = useT();
  const [tab, setTab] = useState<Tab>("uses");
  const pages = usePagedList(`billing-history:${tab}`, (cursor) => api.billingHistory(tab, cursor) as Promise<{ items: unknown[]; next_cursor: string | null }>);
  const items = pages.items;
  return (
    <section className="card">
      <h2>{t.billing.historyTitle}</h2>
      <p className="small muted">{t.billing.historyNote}</p>
      <div className="chips list-tabs" role="group" aria-label={t.billing.historyTitle}>
        {(["uses", "grants", "orders"] as const).map((k) => <button key={k} className={tab === k ? "chip on" : "chip"} aria-pressed={tab === k} onClick={() => setTab(k)}>{t.billing.tabs[k]}</button>)}
      </div>
      {pages.error && <p className="error small" role="alert">{pages.error}</p>}
      {items === null && !pages.error && <p className="muted small">{t.common.loading}</p>}
      {items !== null && items.length === 0 && <p className="muted small">{t.billing.nothingYet}</p>}
      {items !== null && items.length > 0 && (
        <ul className="rows">
          {tab === "uses" && (items as UseRow[]).map((u) => <UseItem key={u.id} u={u} />)}
          {tab === "grants" && (items as GrantRow[]).map((g) => <GrantItem key={g.id} g={g} />)}
          {tab === "orders" && (items as OrderView[]).map((o) => <OrderItem key={o.id} o={o} />)}
        </ul>
      )}
      <ShowMore hasMore={pages.hasMore} busy={pages.loadingMore} onMore={() => void pages.loadMore()} />
    </section>
  );
}

function UseItem({ u }: { u: UseRow }) {
  const t = useT();
  const pending = u.pending_status ? ` · ${t.billing.pendingStatus[u.pending_status] ?? u.pending_status}${u.kind === "reserve" && u.pending_status === "waiting" && u.pending_expires_at ? ` (${formatDay(u.pending_expires_at * 1000)})` : ""}` : "";
  const text = u.kind === "refund" ? t.billing.useRefund(-u.units) : u.kind === "reserve" ? t.billing.useReserve(u.units) : u.units === 0 ? t.billing.useSendFree(u.messages) : t.billing.useSend(u.units, u.messages);
  return <li><span className="grow small">{text}{u.kind !== "send" ? pending : ""}</span><span className="small muted">{formatDay(u.created_at * 1000)}</span></li>;
}

function GrantItem({ g }: { g: GrantRow }) {
  const t = useT();
  return <li><span className="grow small"><strong>{t.billing.grantReasons[g.reason] ?? g.reason}</strong> · {t.billing.grantLine(g.remaining, g.units, formatDay(g.expires_at * 1000))}</span><span className="small muted">{formatDay(g.created_at * 1000)}</span></li>;
}

function OrderItem({ o }: { o: OrderView }) {
  const t = useT();
  return <li><span className="grow small"><strong>{o.price_id}</strong> · {t.billing.orderStatus[o.status] ?? o.status}</span><span className="small muted">{formatDay(o.created_at * 1000)}</span></li>;
}

/** Account page card: the plan and what is left, with the way to Billing. */
export function PlanSummary() {
  const t = useT();
  const { me } = useSession();
  const b = me.billing;
  if (!b) return null;
  return (
    <section className="card plan-summary">
      <h2>{t.billing.accountCard}</h2>
      <p><strong>{t.billing.accountLine(t.billing.planNames[b.plan.id] ?? b.plan.id, b.stamps.remaining)}</strong></p>
      <Meter value={b.stamps.period.consumed} max={b.stamps.period.granted} label={t.billing.monthMeterName} text={t.billing.monthMeter(b.stamps.period.consumed, b.stamps.period.granted)} />
      <p className="small">{t.billing.accountStorage(sizeLabelExact(b.storage.used_bytes), sizeLabelExact(b.storage.quota_bytes))}</p>
      <p className="small"><a href="/billing" onClick={link("/billing")}>{t.billing.manage}</a></p>
    </section>
  );
}
